import React, { useState, useMemo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
  Sphere,
  Graticule
} from 'react-simple-maps';
import { scaleSequential } from 'd3-scale';
import { interpolateBlues, interpolateReds, interpolateGreens, interpolateOranges } from 'd3-scale-chromatic';
import { Box, Typography, Paper, Tooltip, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';

// Geographic data URL - using reliable CDN sources with country features
const WORLD_MAP_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const USA_MAP_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Styled components
const MapContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 400
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 400,
}));

const MapWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  position: 'relative',
  '& svg': {
    width: '100%',
    height: '100%'
  }
}));

const Legend = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  fontSize: '0.75rem'
}));

const LegendScale = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 20,
  width: 200,
  marginRight: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4
}));

const LegendItem = styled(Box)<{ color: string }>(({ color }) => ({
  flex: 1,
  backgroundColor: color
}));



import type { GeographicMapConfig, MapMarker } from '../types/analytics.types';

export interface GeographicMapProps {
  data: any[];
  config: GeographicMapConfig;
  loading?: boolean;
  error?: string;
  className?: string;
}

// Color scale configurations
const COLOR_SCALES = {
  blues: interpolateBlues,
  reds: interpolateReds,
  greens: interpolateGreens,
  oranges: interpolateOranges
};

export const GeographicMap: React.FC<GeographicMapProps> = ({
  data = [],
  config,
  loading = false,
  error,
  className
}) => {
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [hoveredGeography, setHoveredGeography] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [matchLogged, setMatchLogged] = useState(false);
  const [debugCount, setDebugCount] = useState(0);


  // Determine map URL based on config
  const mapUrl = useMemo(() => {
    if (config.mapUrl) return config.mapUrl;
    if (config.mapType === 'usa') return USA_MAP_URL;
    return WORLD_MAP_URL;
  }, [config.mapType, config.mapUrl]);

  // Create data lookup for geographic regions
  const dataLookup = useMemo(() => {
    const lookup: Record<string, any> = {};
    data.forEach(item => {
      // Try multiple possible keys for geographic identification
      const geoKey = item.country_code || item.state_code || item.region_code || 
                    item.country || item.state || item.region || item.id || item.name;
      if (geoKey) {
        const key = geoKey.toString().toUpperCase();
        lookup[key] = item;
        
        // Add common country code variations for better matching with visionscarto atlas
        if (item.country_code) {
          const code = item.country_code.toString().toUpperCase();
          lookup[code] = item;
          
          // Add visionscarto-specific country name variations
          const countryNameMappings: Record<string, string> = {
            'US': 'United States of America',
            'GB': 'United Kingdom', 
            'DE': 'Germany',
            'FR': 'France',
            'JP': 'Japan',
            'CA': 'Canada',
            'AU': 'Australia',
            'BR': 'Brazil',
            'MX': 'Mexico',
            'CN': 'China'
          };
          
          // Add ISO3 variations
          const iso3Mappings: Record<string, string> = {
            'US': 'USA',
            'GB': 'GBR', 
            'DE': 'DEU',
            'FR': 'FRA',
            'JP': 'JPN',
            'CA': 'CAN',
            'AU': 'AUS',
            'BR': 'BRA',
            'MX': 'MEX',
            'CN': 'CHN'
          };
          
          if (countryNameMappings[code]) {
            lookup[countryNameMappings[code].toUpperCase()] = item;
          }
          if (iso3Mappings[code]) {
            lookup[iso3Mappings[code]] = item;
          }
        }
      }
    });
    
    // Only log if we have data but no matches (potential issue)
    if (data.length > 0 && Object.keys(lookup).length === 0) {
      console.warn('🗺️ No geography lookups created from data:', data);
    }
    return lookup;
  }, [data]);

  // Create color scale
  const colorScale = useMemo(() => {
    if (!config.colorProperty || !config.colorDomain) return null;
    
    const interpolator = COLOR_SCALES[config.colorScheme || 'blues'];
    return scaleSequential(interpolator).domain(config.colorDomain);
  }, [config.colorProperty, config.colorDomain, config.colorScheme]);

  // Get fill color for a geography
  const getFillColor = (geo: any) => {
    if (!colorScale || !config.colorProperty) {
      return '#f0f0f0';
    }

    // Try to match geography with data using multiple possible identifiers
    // world-atlas@2 format properties
    const possibleIds = [
      geo.id,
      geo.properties?.name,           // world-atlas format
      geo.properties?.NAME,
      geo.properties?.NAME_EN, 
      geo.properties?.ADMIN,
      geo.properties?.ISO_A2,
      geo.properties?.ISO_A3,
      geo.properties?.ADM0_A3
    ].filter(Boolean);
    
         let dataPoint = null;
     let matchedKey = null;
     
     for (const geoId of possibleIds) {
       if (geoId) {
         const key = geoId.toString().toUpperCase();
         dataPoint = dataLookup[key];
         if (dataPoint) {
           matchedKey = key;
           break;
         }
         
         // If not found and geoId is numeric, try ISO numeric to country code conversion
         if (!dataPoint && /^\d+$/.test(geoId.toString())) {
           const countryCode = isoNumericToCountryCode[geoId.toString()];
           if (countryCode) {
             dataPoint = dataLookup[countryCode];
             if (dataPoint) {
               matchedKey = countryCode;
               break;
             }
           }
         }
       }
     }
     
     
     if (!dataPoint) {
       return '#f0f0f0';
     }

    const value = dataPoint[config.colorProperty];
    return value !== undefined ? colorScale(value) : '#f0f0f0';
  };

  // ISO numeric to country code mapping
  const isoNumericToCountryCode: Record<string, string> = {
    '840': 'US',  // United States
    '124': 'CA',  // Canada
    '036': 'AU',  // Australia
    '076': 'BR',  // Brazil
    '156': 'CN',  // China
    '250': 'FR',  // France
    '276': 'DE',  // Germany
    '356': 'IN',  // India
    '392': 'JP',  // Japan
    '484': 'MX',  // Mexico
    '826': 'GB',  // United Kingdom
    '528': 'NL',  // Netherlands
    '724': 'ES',  // Spain
    '380': 'IT',  // Italy
    '643': 'RU',  // Russia
    '410': 'KR',  // South Korea
    '702': 'SG',  // Singapore
    '752': 'SE',  // Sweden
    '578': 'NO',  // Norway
    '208': 'DK',  // Denmark
    '246': 'FI',  // Finland
    '040': 'AT',  // Austria
    '056': 'BE',  // Belgium
    '756': 'CH',  // Switzerland
    '620': 'PT',  // Portugal
    '372': 'IE',  // Ireland
    '554': 'NZ',  // New Zealand
    '710': 'ZA',  // South Africa
    '784': 'AE',  // United Arab Emirates
    '682': 'SA',  // Saudi Arabia
    '368': 'IQ',  // Iraq
    '364': 'IR',  // Iran
    '050': 'BD',  // Bangladesh
    '586': 'PK',  // Pakistan
    '764': 'TH',  // Thailand
    '704': 'VN',  // Vietnam
    '360': 'ID',  // Indonesia
    '458': 'MY',  // Malaysia
    '608': 'PH',  // Philippines
  };

  // Format value as currency
  const formatCurrency = (value: any): string => {
    if (value == null || isNaN(Number(value))) return String(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(value));
  };

  // Handle geography mouse events
  const handleGeographyEnter = (geo: any) => {
    const geoId = geo.id || geo.properties?.NAME || geo.properties?.NAME_EN || 
                 geo.properties?.ADMIN || geo.properties?.iso_a2;
    
    setHoveredGeography(geoId);
    
    if (geoId) {
      // Try to find data using multiple lookup strategies
      let dataPoint;
      let lookupKey = geoId.toString().toUpperCase();
      
      // First try direct lookup
      dataPoint = dataLookup[lookupKey];
      
      // If not found and geoId is numeric, try ISO numeric to country code conversion
      if (!dataPoint && /^\d+$/.test(geoId.toString())) {
        const countryCode = isoNumericToCountryCode[geoId.toString()];
        if (countryCode) {
          dataPoint = dataLookup[countryCode];
          lookupKey = countryCode;
        }
      }
       
      if (dataPoint && config.tooltipField) {
        // Always use the geography properties for the display name, not the ID
        const name = geo.properties?.NAME || geo.properties?.NAME_EN || 
                    geo.properties?.ADMIN || geo.properties?.name || 
                    lookupKey; // fallback to the lookup key if no name properties available
        const value = dataPoint[config.tooltipField];
        const formattedValue = formatCurrency(value);
        setTooltipContent(`${name}: ${formattedValue}`);
      } else {
        const name = geo.properties?.NAME || geo.properties?.NAME_EN || 
                    geo.properties?.ADMIN || geo.properties?.name || geoId;
        setTooltipContent(name);
      }
    }
  };

  const handleGeographyLeave = () => {
    setHoveredGeography(null);
    setTooltipContent('');
  };

  // Create legend items
  const legendItems = useMemo(() => {
    if (!colorScale || !config.legend?.show) return [];
    
    const items = [];
    const steps = 10;
    const [min, max] = config.colorDomain || [0, 100];
    
    for (let i = 0; i < steps; i++) {
      const value = min + (max - min) * (i / (steps - 1));
      items.push({
        color: colorScale(value),
        value: value
      });
    }
    return items;
  }, [colorScale, config.legend, config.colorDomain]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} />
      </LoadingContainer>
    );
  }

  if (error || mapError) {
    return (
      <Alert severity="error">
        {error || mapError}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="warning">
        No geographic data available for visualization
      </Alert>
    );
  }

  return (
    <MapContainer className={className}>
      {config.legend?.show && config.legend.position === 'top' && (
        <Legend>
          {config.legend.title && (
            <Typography variant="caption" sx={{ mr: 2 }}>
              {config.legend.title}
            </Typography>
          )}
          <LegendScale>
            {legendItems.map((item, index) => (
              <LegendItem key={index} color={item.color} />
            ))}
          </LegendScale>
          <Typography variant="caption" sx={{ ml: 1 }}>
            {config.colorDomain?.[0]} - {config.colorDomain?.[1]}
          </Typography>
        </Legend>
      )}
      
      <MapWrapper>
        <ComposableMap projection={config.projection || 'geoNaturalEarth1'}>
          {config.zoomable ? (
            <ZoomableGroup>
              {config.showSphere && <Sphere id="sphere" fill="transparent" stroke="#E4E5E6" strokeWidth={0.5} />}
              {config.showGraticule && <Graticule stroke="#E4E5E6" strokeWidth={0.5} />}
              
              <Geographies geography={mapUrl}>
                {({ geographies }) => {
                  return geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getFillColor(geo)}
                      stroke="#D6D6DA"
                      strokeWidth={0.5}
                      style={{
                        default: {
                          outline: 'none',
                        },
                        hover: {
                          fill: hoveredGeography === (geo.id || geo.properties?.NAME || geo.properties?.NAME_EN || geo.properties?.ADMIN || geo.properties?.iso_a2) 
                                ? '#ff6b6b' : getFillColor(geo),
                          outline: 'none',
                          strokeWidth: 1
                        },
                        pressed: {
                          outline: 'none',
                        }
                      }}
                      onMouseEnter={() => handleGeographyEnter(geo)}
                      onMouseLeave={handleGeographyLeave}
                    />
                  ));
                }}
              </Geographies>

              {/* Render markers if provided */}
              {config.markers?.map((marker) => (
                <Marker key={marker.id} coordinates={marker.coordinates}>
                  <circle
                    r={marker.size || 4}
                    fill={marker.color || '#ff6b6b'}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                  <text
                    textAnchor="middle"
                    y={marker.size ? marker.size + 20 : 24}
                    style={{ 
                      fontFamily: 'system-ui', 
                      fontSize: '14px', 
                      fill: '#1a1a1a',
                      fontWeight: '600',
                      stroke: '#ffffff',
                      strokeWidth: '3px',
                      paintOrder: 'stroke fill'
                    }}
                  >
                    {marker.name}
                  </text>
                </Marker>
              ))}
            </ZoomableGroup>
          ) : (
            <>
              {config.showSphere && <Sphere id="sphere2" fill="transparent" stroke="#E4E5E6" strokeWidth={0.5} />}
              {config.showGraticule && <Graticule stroke="#E4E5E6" strokeWidth={0.5} />}
              
              <Geographies geography={mapUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getFillColor(geo)}
                      stroke="#D6D6DA"
                      strokeWidth={0.5}
                      style={{
                        default: {
                          outline: 'none',
                        },
                        hover: {
                          fill: hoveredGeography === (geo.id || geo.properties?.NAME || geo.properties?.NAME_EN || geo.properties?.ADMIN || geo.properties?.iso_a2) 
                                ? '#ff6b6b' : getFillColor(geo),
                          outline: 'none',
                          strokeWidth: 1
                        },
                        pressed: {
                          outline: 'none',
                        }
                      }}
                      onMouseEnter={() => handleGeographyEnter(geo)}
                      onMouseLeave={handleGeographyLeave}
                    />
                  ))
                }
              </Geographies>

              {/* Render markers if provided */}
              {config.markers?.map((marker) => (
                <Marker key={marker.id} coordinates={marker.coordinates}>
                  <circle
                    r={marker.size || 4}
                    fill={marker.color || '#ff6b6b'}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                  <text
                    textAnchor="middle"
                    y={marker.size ? marker.size + 20 : 24}
                    style={{ 
                      fontFamily: 'system-ui', 
                      fontSize: '14px', 
                      fill: '#1a1a1a',
                      fontWeight: '600',
                      stroke: '#ffffff',
                      strokeWidth: '3px',
                      paintOrder: 'stroke fill'
                    }}
                  >
                    {marker.name}
                  </text>
                </Marker>
              ))}
            </>
          )}
        </ComposableMap>

        {/* Tooltip */}
        {tooltipContent && (
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              left: 20,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: 2,
              borderRadius: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              pointerEvents: 'none',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {tooltipContent}
          </Box>
        )}
      </MapWrapper>

      {config.legend?.show && config.legend.position !== 'top' && (
        <Legend>
          {config.legend.title && (
            <Typography variant="caption" sx={{ mr: 2 }}>
              {config.legend.title}
            </Typography>
          )}
          <LegendScale>
            {legendItems.map((item, index) => (
              <LegendItem key={index} color={item.color} />
            ))}
          </LegendScale>
          <Typography variant="caption" sx={{ ml: 1 }}>
            {config.colorDomain?.[0]} - {config.colorDomain?.[1]}
          </Typography>
        </Legend>
      )}
    </MapContainer>
  );
}; 